import * as PIXI from 'pixi.js';

import type { SeekMode } from '../../../enums';
import { Config } from '../../../video-data/config';
import { getPositionFromTransformation } from '../../../video-data/designs/scenes';
import type { LoadSettings } from '../../serializableMixin';
import { DynamicStylingMixin } from '../../dynamicStylingMixin';
import type { BaseAssetSettings } from '../BaseAsset';
import { BaseAsset } from '../BaseAsset';
import { resetSprite } from '../utils';
import { StringAttribute } from '../../attributes';
import { ChartRenderer } from './ChartRenderer';
import type { ChartConfig, ChartPalette, ChartSeriesType } from './types';

type ChartAssetSettings = BaseAssetSettings;

export class ChartAsset extends DynamicStylingMixin(BaseAsset) {
    override _classType = Config.CHART_ASSET_TYPE;

    #width = 800;
    #height = 600;
    #config: ChartConfig | null = null;
    #progress = 1;

    #sprite: PIXI.Sprite;
    #renderer: ChartRenderer;

    constructor(container, settings: ChartAssetSettings) {
        super(container, settings);

        this.#renderer = new ChartRenderer();
        this.#sprite = new PIXI.Sprite();
        this._addPixiChild(this.#sprite);
    }

    override destroy() {
        this.#renderer.destroy();
        super.destroy(false);
        this.#sprite.destroy(false);
    }

    override get failedToLoad() {
        return false;
    }

    override get isLoaded() {
        return true;
    }

    override get _shouldLoad() {
        return true;
    }

    override get width() {
        return this.#width;
    }

    override set width(value: number) {
        if (this.#width === value) {
            return;
        }
        this.#width = value;
        this.requiresRedraw = true;
    }

    override get height() {
        return this.#height;
    }

    override set height(value: number) {
        if (this.#height === value) {
            return;
        }
        this.#height = value;
        this.requiresRedraw = true;
    }

    get maxWidth() {
        return this.#width;
    }

    set maxWidth(value: number) {
        this.width = value;
    }

    get maxHeight() {
        return this.#height;
    }

    set maxHeight(value: number) {
        this.height = value;
    }

    getVisibleResolution(): { width: number; height: number } {
        return { width: this.#width, height: this.#height };
    }

    override get approximateMaxBounds(): PIXI.Rectangle {
        const freeParent = this.getFreelyPositionedParent();
        const transformation = freeParent.transformation;
        const { width, height } = this.getVisibleResolution();
        const { x, y } = getPositionFromTransformation(transformation, width, height);
        return { x, y, width, height } as PIXI.Rectangle;
    }

    get config(): ChartConfig | null {
        return this.#config;
    }

    set config(value: ChartConfig | null) {
        this.#config = value;
        this.requiresRedraw = true;
    }

    @StringAttribute()
    accessor chartType: ChartSeriesType | '' = '';

    @StringAttribute()
    accessor chartData = '';

    @StringAttribute()
    accessor chartPrompt = '';

    #getPalette(): ChartPalette {
        const colors: string[] = [];
        if (this.template?.colors) {
            for (const templateColor of this.template.colors) {
                const hexColor = '#' + templateColor.color.toString(16).padStart(6, '0');
                colors.push(hexColor);
            }
        }
        return {
            colors,
            background: 'transparent',
            text: '#8b949e',
            axis: '#30363d',
            grid: '#21262d',
        };
    }

    override _hasTemplateReferenceValues(): boolean {
        if (!this.#config) {
            return false;
        }
        return true;
    }

    override updateForRender() {
        if (!this.visible || !this.#config) {
            return;
        }

        this.#sprite.texture = this.root._reusableTextures.allocateTexture(() => {
            resetSprite(this.#sprite);
        });

        const canvas = (this.#sprite.texture.baseTexture.resource as PIXI.BaseImageResource)
            .source as HTMLCanvasElement;

        const palette = this.#getPalette();
        this.#renderer.renderToCanvas(canvas, this.#config, this.#progress, palette);

        this.#sprite.width = this.#width;
        this.#sprite.height = this.#height;

        this.#sprite.texture.baseTexture.resource.update();
    }

    override _build() {
        this.clearAnimation();

        if (!this.#config) {
            return;
        }

        this.#sprite.width = this.#width;
        this.#sprite.height = this.#height;

        if (this.hasDynamicStyle || this.hasMorphAnimation) {
            this._prepareDynamicStylingForUpdate();
            this.applyTransformation();
            this._applyAnimation();
            this._updateDynamicStyling();
        } else {
            this.applyTransformation();
            this._applyAnimation();
        }

        this._applyIntroAnimations();
        this._applyOutroAnimations();
        this.animationManager.setInitValues();
    }

    override _draw() {
        if (!this.#config) {
            return;
        }

        this.#sprite.width = this.#width;
        this.#sprite.height = this.#height;
        this.applyTransformation();
    }

    override _seek(progress: number, seekMode: SeekMode) {
        super._seek(progress, seekMode);

        if (!this.#config) return;

        const animationDuration = 1500;
        const sceneDuration = this.container?.duration ?? animationDuration;
        const animationFraction = Math.min(1, animationDuration / sceneDuration);
        const rawChartProgress = Math.min(1, Math.max(0, progress) / animationFraction);
        const quantized = Math.round(rawChartProgress * 50) / 50;

        this.#progress = Math.max(0, Math.min(1, quantized));
    }

    _configCustomSave() {
        return this.#config;
    }

    _configCustomLoad(configData: ChartConfig | null, _settings: LoadSettings) {
        this.#config = configData;
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            'mask',
            'maxWidth',
            'maxHeight',
            'config',
            'chartType',
            'chartData',
            'chartPrompt',
        ]);
    }
}
